import {
  safeParseBrandingDefinition,
  type BrandingDefinition,
} from "@lemn-ltd/brand-contract";
import type {
  SystemBrandingDto,
  SystemBrandingSummaryDto,
} from "../../api/generated.ts";

export type SystemBrandingGalleryRecord = {
  readonly summary: SystemBrandingSummaryDto;
  readonly detail?: SystemBrandingDto;
  readonly definition?: BrandingDefinition;
  readonly density?: string;
  readonly typography?: string;
};

export function createSystemBrandingGalleryRecord(
  summary: SystemBrandingSummaryDto,
  detail?: SystemBrandingDto,
): SystemBrandingGalleryRecord {
  if (!detail) return { summary };
  const parsed = safeParseBrandingDefinition(detail.definition);
  if (!parsed.success) return { summary, detail };
  return {
    summary,
    detail,
    definition: parsed.data,
    density: densityFacet(parsed.data),
    typography: typographyFacet(parsed.data),
  };
}

export function filterSystemBrandingGallery(
  records: readonly SystemBrandingGalleryRecord[],
  filters: {
    readonly query: string;
    readonly category: string;
    readonly density: string;
    readonly typography: string;
  },
): readonly SystemBrandingGalleryRecord[] {
  const normalizedQuery = filters.query.trim().toLocaleLowerCase();
  return records.filter((record) => {
    if (filters.category !== "all" && record.summary.category !== filters.category) return false;
    if (filters.density !== "all" && record.density !== filters.density) return false;
    if (filters.typography !== "all" && record.typography !== filters.typography) return false;
    if (!normalizedQuery) return true;
    return [
      record.summary.name,
      record.summary.description,
      record.summary.id,
      record.summary.category,
      record.density ?? "",
      record.typography ?? "",
      ...record.summary.tags,
    ].some((value) => value.toLocaleLowerCase().includes(normalizedQuery));
  });
}

function densityFacet(definition: BrandingDefinition): string {
  return [...new Set(
    Object.values(definition.modes).map(
      (mode) => mode.spacingAndDensity.density,
    ),
  )].sort().join(" + ");
}

function typographyFacet(definition: BrandingDefinition): string {
  const body = definition.typography.body.ref;
  const heading = definition.typography.heading;
  if (heading.source === "inherit" || heading.ref === body) return body;
  return `${body} + ${heading.ref}`;
}
